// Copyright 2025, University of Maryland, All Rights Reserved
import { v4 as uuidv4 } from 'uuid'
import type { JSONSchema, JSONSchemaProperty } from '@/api/types/schema'
import type { SchemaBuilderSection, SchemaBuilderState } from '@/types/schema-builder'
import type { SchemaField, SchemaFieldType } from '@/types/schema-fields'
import { getFieldTypeConfig } from '@/utils/schema/schema-type-utils'

/**
 * Generates a JSON schema from schema sections with multiple field types
 * Embeds UI metadata as const values for AI response rendering
 */
export function generateSchemaFromSections(sections: SchemaBuilderSection[], dialogTitle: string = 'Analysis Results'): JSONSchema {
  const properties: Record<string, JSONSchemaProperty> = {}
  const required: string[] = []
  
  // Note: Summary section logic is handled in the useSchemaState hook
  // This function just processes the sections as provided
  // Sort sections by order to ensure consistent schema property order
  const processedSections = [...sections].sort((a, b) => a.order - b.order)
  
  processedSections.forEach(section => {
    const sectionProperties: Record<string, JSONSchemaProperty> = {}
    const sectionRequired: string[] = []
    
    // Only include icon if one is selected (as a property that AI should return)
    if (section.icon) {
      sectionProperties.icon = { type: "string", const: section.icon }
      sectionRequired.push("icon")
    }
    
    // Process section fields (sort by order to maintain field order)
    const sorted_fields = [...section.fields].sort((a, b) => a.order - b.order)
    
    sorted_fields.forEach(field => {
      let field_property: JSONSchemaProperty
      
      switch (field.field_type) {
        case 'string':
          field_property = {
            type: "string"
          }
          break
        case 'integer':
          field_property = {
            type: "integer",
            minimum: field.config.min ?? 1,
            maximum: field.config.max ?? 10
          }
          break
        case 'simple_array':
          field_property = {
            type: "array",
            items: { type: "string" }
          }
          break
        case 'object_array': {
          const object_properties: Record<string, JSONSchemaProperty> = {}
          if (field.config.properties) {
            Object.entries(field.config.properties).forEach(([prop_key, prop_config]) => {
              let prop_schema: JSONSchemaProperty
              
              if (prop_config.type === 'string') {
                prop_schema = { type: "string" }
              } else if (prop_config.type === 'integer') {
                prop_schema = { 
                  type: "integer",
                  minimum: prop_config.min ?? 0,
                  maximum: prop_config.max ?? 1000
                }
              } else if (prop_config.type === 'enum_field') {
                prop_schema = {
                  type: "string",
                  enum: prop_config.enum_options || []
                }
              } else if (prop_config.type === 'score') {
                prop_schema = {
                  type: "object",
                  additionalProperties: false,
                  properties: {
                    score: {
                      type: "integer",
                      minimum: prop_config.score_min ?? 0,
                      maximum: prop_config.score_max ?? 10
                    },
                    score_type: {
                      type: "string",
                      enum: prop_config.type_options || ['positive', 'neutral', 'negative']
                    }
                  },
                  required: ["score", "score_type"]
                }
              } else {
                prop_schema = { type: "string" }
              }
              
              // Add title from title if available
              if (prop_config.title) {
                prop_schema.title = prop_config.title
              }
              
              // Add description if available
              if (prop_config.description) {
                prop_schema.description = prop_config.description
              }
              
              object_properties[prop_key] = prop_schema
            })
          }
          field_property = {
            type: "array",
            items: {
              type: "object",
              additionalProperties: false,
              properties: object_properties,
              required: Object.keys(object_properties)
            }
          }
          break
        }
        case 'enum_field':
          field_property = {
            type: "string",
            enum: field.config.enum_options || []
          }
          break
        case 'score':
          field_property = {
            type: "object",
            additionalProperties: false,
            properties: {
              score: {
                type: "integer",
                minimum: field.config.score_min ?? 0,
                maximum: field.config.score_max ?? 10
              },
              score_type: {
                type: "string",
                enum: field.config.type_options || ['positive', 'neutral', 'negative']
              }
            },
            required: ["score", "score_type"]
          }
          break
        default:
          field_property = { type: "string" }
      }
      
      // Add field description if provided
      if (field.description) {
        field_property.description = field.description
      }
      
      // Add field title as direct property if provided
      if (field.title) {
        field_property.title = field.title
      }
      
      // Add field order as metadata (for preserving order when extracting)
      (field_property as any).order = field.order
      
      sectionProperties[field.id] = field_property
      sectionRequired.push(field.id)
    })
    
    properties[section.id] = {
      type: "object",
      title: section.title,
      additionalProperties: false,
      properties: sectionProperties,
      required: sectionRequired,
      ...(section.order !== undefined && { order: section.order })  // Add order as custom property
    } as JSONSchemaProperty
    required.push(section.id)
  })
  
  return {
    type: "object",
    title: dialogTitle,
    additionalProperties: false,
    properties,
    required
  }
}

/**
 * Validates a schema generator state
 */
export function validateSchemaGeneratorState(state: SchemaBuilderState): { valid: boolean; errors: string[] } {
  const errors: string[] = []

  if (state.sections.length === 0) {
    errors.push("At least one analysis section is required")
  }

  state.sections.forEach((section, index) => {
    if (!section.id.trim()) {
      errors.push(`Section ${index + 1} must have an ID`)
    }
    
    if (!section.title.trim()) {
      errors.push(`Section "${section.id}" must have a title`)
    }
    
    if (section.fields.length === 0) {
      errors.push(`Section "${section.title}" needs at least one field`)
    }
    
    section.fields.forEach((field, fieldIndex) => {
      if (!field.id.trim()) {
        errors.push(`Field ${fieldIndex + 1} in section "${section.title}" must have an ID`)
      }
      
      // Title is now optional, so only validate if provided
      if (field.title && !field.title.trim()) {
        errors.push(`Field "${field.id}" in section "${section.title}" has empty title`)
      }
    })
  })
  
  return { valid: errors.length === 0, errors }
}

/**
 * Pretty prints a JSON schema for display with stable serialization
 */
export function formatSchemaForDisplay(schema: JSONSchema): string {
  // Use a replacer function to ensure consistent number formatting
  return JSON.stringify(schema, (_key, value) => {
    // Ensure numbers are always serialized as numbers, not strings
    if (typeof value === 'number' && !isNaN(value)) {
      return value
    }
    // Convert string numbers back to numbers for schema properties
    if (typeof value === 'string' && /^\d+$/.test(value)) {
      const num = parseInt(value, 10)
      if (!isNaN(num)) {
        return num
      }
    }
    return value
  }, 2)
}

/**
 * Generates a summary of what the schema contains for user feedback
 */
export function generateSchemaSummary(schema: JSONSchema): string {
  const allKeys = Object.keys(schema.properties || {})
  const sections = allKeys
  
  if (sections.length === 0) {
    return "No analysis sections configured"
  }

  let summary = `Schema includes ${sections.length} analysis section${sections.length > 1 ? 's' : ''}:\n`
  
  for (const section of sections) {
    const sectionProps = schema.properties[section].properties || {}
    // Count field properties (exclude UI metadata properties)
    const fieldCount = Object.keys(sectionProps).filter(key => 
      key !== 'icon' && key !== 'order'
    ).length
    
    summary += `• ${section}: ${fieldCount} field${fieldCount > 1 ? 's' : ''}\n`
  }

  return summary.trim()
}

/**
 * Create a new empty section for the builder
 */
export function createNewSection(
  title: string = '',
  existingSections: SchemaBuilderSection[] = []
): SchemaBuilderSection {
  const generateSectionId = (title: string): string => {
    if (!title) return 'untitled'
    
    const baseId = title.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 50)
    
    // Make sure the ID is unique
    let finalId = baseId
    let counter = 1
    while (existingSections.some(s => s.id === finalId)) {
      finalId = `${baseId}_${counter}`
      counter++
    }
    
    return finalId
  }

  const getNextOrder = (sections: SchemaBuilderSection[]): number => {
    if (sections.length === 0) return 1
    return Math.max(...sections.map(s => s.order)) + 1
  }

  const sectionId = generateSectionId(title)
  const order = getNextOrder(existingSections)
  
  return {
    uuid: uuidv4(),
    id: sectionId,
    title,
    icon: undefined,
    order,
    fields: []
  }
}

/**
 * Create default summary section with filter_score field for monitor schemas
 */
export function createDefaultSummarySection(): SchemaBuilderSection {
  const score_field = createNewField('score', 'Filter Score', [])
  score_field.id = 'filter_score'
  score_field.title = 'Filter Score'
  score_field.description = 'Overall scoring and score_type classification for this analysis'
  
  return {
    uuid: uuidv4(),
    id: 'summary',
    title: 'Summary',
    icon: 'Target',
    order: 0,
    fields: [score_field]
  }
}

/**
 * Create a new field for enhanced schemas
 */
export function createNewField(
  field_type: SchemaFieldType,
  title: string = '',
  existing_fields: SchemaField[] = []
): SchemaField {
  const generateFieldId = (title: string): string => {
    if (!title) return 'field'
    
    const baseId = title.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 30)
    
    // Make sure the ID is unique
    let finalId = baseId
    let counter = 1
    while (existing_fields.some(f => f.id === finalId)) {
      finalId = `${baseId}_${counter}`
      counter++
    }
    
    return finalId
  }

  const getNextOrder = (fields: SchemaField[]): number => {
    if (fields.length === 0) return 0
    return Math.max(...fields.map(f => f.order)) + 1
  }

  const config = getFieldTypeConfig(field_type)
  const order = getNextOrder(existing_fields)
  
  return {
    uuid: uuidv4(),
    id: generateFieldId(title || config.title),
    title: title || undefined,
    field_type,
    config,
    order
  }
}
